import React from 'react';
import { Plus, Trash2, Image } from 'lucide-react';
import type { NavbarData, NavbarLogo, Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from '../shared/PageSelector';
import { clampLogoImageSizeForRender } from '../../LogoUploadModal/useLogoUpload';
import { isLegacyBothLogo, prepareLogoForEditor } from '../../../utils/navbarLogo';

// Logo Preview and Display Components

interface LogoPreviewProps {
  data: {
    siteName?: string;
    logo?: NavbarLogo;
  };
  onEdit: () => void;
  onRemove: () => void;
  onSiteNameChange: (value: string) => void;
}

export const LogoPreview: React.FC<LogoPreviewProps> = ({
  data,
  onEdit,
  onRemove,
  onSiteNameChange,
}) => (
  <div className="space-y-4">
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">Current Logo</span>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onEdit}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Remove
          </button>
        </div>
      </div>

      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
        <LogoDisplay logo={data.logo!} />
        <div className="flex-1 text-right">
          <span className="text-xs text-gray-500 capitalize">{data.logo?.mode} logo</span>
        </div>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Fallback Site Name (optional)
      </label>
      <input
        type="text"
        value={data.siteName || ''}
        onChange={e => onSiteNameChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Used if logo fails to load"
      />
      <p className="mt-1 text-xs text-gray-500">
        This text will be displayed if the logo fails to load or for accessibility.
      </p>
    </div>
  </div>
);

interface LogoDisplayProps {
  logo: NavbarLogo;
}

const getTextSizeClass = (size?: string) => {
  switch (size) {
    case 'small':
      return 'text-lg';
    case 'large':
      return 'text-2xl';
    case 'xl':
      return 'text-3xl';
    default:
      return 'text-xl';
  }
};

const getTextWeightClass = (weight?: string) => {
  const weightClasses: Record<string, string> = {
    light: 'font-light',
    normal: 'font-normal',
    bold: 'font-bold',
  };
  return weightClasses[weight || 'bold'] || 'font-bold';
};

export const LogoDisplay: React.FC<LogoDisplayProps> = ({ logo }) => {
  if (isLegacyBothLogo(logo)) {
    const imageBox =
      logo.imageUrl || logo.cloudfrontUrl
        ? clampLogoImageSizeForRender(logo.imageWidth, logo.imageHeight, 'legacy-both')
        : null;

    return (
      <div className="flex items-center flex-row gap-2 max-h-14 overflow-hidden">
        {imageBox && (
          <img
            src={logo.cloudfrontUrl || logo.imageUrl}
            alt="Logo preview"
            className="object-contain flex-shrink-0"
            style={{
              width: `${imageBox.width}px`,
              height: `${imageBox.height}px`,
              maxWidth: `${imageBox.maxWidth}px`,
              maxHeight: `${imageBox.maxHeight}px`,
            }}
          />
        )}
        {logo.text && (
          <span
            className={`${getTextWeightClass(logo.textWeight)} text-base truncate max-w-[180px]`}
            style={{ color: logo.textColor || '#000000' }}
          >
            {logo.text}
          </span>
        )}
      </div>
    );
  }

  const editorLogo = prepareLogoForEditor(logo).logoData;

  if (editorLogo.mode === 'text' && editorLogo.text) {
    return (
      <span
        className={`${getTextWeightClass(editorLogo.textWeight)} ${getTextSizeClass(editorLogo.textSize)}`}
        style={{ color: editorLogo.textColor || '#000000' }}
      >
        {editorLogo.text}
      </span>
    );
  }

  if (editorLogo.mode === 'image' && (editorLogo.imageUrl || editorLogo.cloudfrontUrl)) {
    const { width, height, maxWidth, maxHeight } = clampLogoImageSizeForRender(
      editorLogo.imageWidth,
      editorLogo.imageHeight,
      'image'
    );
    return (
      <img
        src={editorLogo.cloudfrontUrl || editorLogo.imageUrl}
        alt="Logo preview"
        className="object-contain"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: `${maxWidth}px`,
          maxHeight: `${maxHeight}px`,
        }}
      />
    );
  }

  return null;
};

interface NoLogoSectionProps {
  siteName?: string;
  onSiteNameChange: (value: string) => void;
  onAddLogo: () => void;
}

export const NoLogoSection: React.FC<NoLogoSectionProps> = ({
  siteName,
  onSiteNameChange,
  onAddLogo,
}) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
      <input
        type="text"
        value={siteName || ''}
        onChange={e => onSiteNameChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Enter site name"
      />
    </div>

    <button
      type="button"
      onClick={onAddLogo}
      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
    >
      <Image size={20} />
      <span>Add Logo Instead</span>
    </button>
    <p className="text-xs text-gray-500 text-center">
      Upload an image logo or configure a text+image combination
    </p>
  </div>
);

// Navigation Links Section

interface NavigationLinksSectionProps {
  links: NavbarData['links'];
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onAddLink: () => void;
  onUpdateLink: (index: number, updates: Partial<NavbarData['links'][0]>) => void;
  onRemoveLink: (index: number) => void;
}

export const NavigationLinksSection: React.FC<NavigationLinksSectionProps> = ({
  links,
  pages,
  isLoadingPages,
  currentPageComponents,
  onAddLink,
  onUpdateLink,
  onRemoveLink,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Navigation Links</label>

    <div className="space-y-3">
      {(links || []).length === 0 && (
        <p className="text-sm text-gray-400 italic">No navigation links yet</p>
      )}
      {(links || []).map((link, index) => (
        <div key={index} className="space-y-2 p-3 bg-gray-50 rounded-lg">
          <div className="flex space-x-2 items-center">
            <input
              type="text"
              value={link.text}
              onChange={e => onUpdateLink(index, { text: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Link text"
            />
            <PageSelector
              value={link.url}
              onChange={url => onUpdateLink(index, { url })}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
            />
            <input
              type="number"
              value={link.order ?? index}
              onChange={e => {
                const value = e.target.value;
                const numValue = value === '' ? 0 : parseInt(value, 10);
                onUpdateLink(index, { order: !isNaN(numValue) ? numValue : 0 });
              }}
              className="w-16 px-2 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Order"
              min="0"
            />
            <button
              type="button"
              onClick={() => onRemoveLink(index)}
              className="p-2 text-red-600 hover:text-red-700"
            >
              <Trash2 size={16} />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-xs text-gray-500">Style:</label>
            <select
              value={link.style || 'normal'}
              onChange={e =>
                onUpdateLink(index, { style: e.target.value as 'normal' | 'button' | 'outline' })
              }
              className="w-36 px-2 py-1 pr-8 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="normal">Normal Link</option>
              <option value="button">Button (Solid)</option>
              <option value="outline">Button (Outline)</option>
            </select>
            {(link.style === 'button' || link.style === 'outline') && (
              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">CTA</span>
            )}
          </div>
        </div>
      ))}
    </div>

    <button
      type="button"
      onClick={onAddLink}
      className={`${(links || []).length > 0 ? 'mt-3' : 'mt-1'} w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center space-x-1`}
    >
      <Plus size={16} />
      <span>Add Link</span>
    </button>
  </div>
);

// Alignment Section

interface AlignmentSectionProps {
  alignment?: string;
  sticky?: boolean;
  onAlignmentChange: (value: string) => void;
  onStickyChange: (value: boolean) => void;
}

export const AlignmentSection: React.FC<AlignmentSectionProps> = ({
  alignment,
  sticky,
  onAlignmentChange,
  onStickyChange,
}) => (
  <div className="grid grid-cols-1 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Link Alignment</label>
      <select
        value={alignment || 'space-between'}
        onChange={e => onAlignmentChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="left">Left (next to brand)</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
        <option value="space-between">Right (space between)</option>
      </select>
      <p className="mt-1 text-sm text-gray-500">
        Brand/logo always stays on the left. This controls where navigation links appear.
      </p>
    </div>

    <div>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={sticky !== false}
          onChange={e => onStickyChange(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm font-medium text-gray-700">Sticky navigation</span>
      </label>
      <p className="mt-1 text-xs text-gray-500">
        Keep the navigation bar visible at the top when scrolling.
      </p>
    </div>
  </div>
);
